import React from 'react';
import classNames, { Argument } from 'classnames';

import { downloadURL } from 'utils/downloadUtils';
import { AbstractMedia } from 'types/AbstractMedia';
import { isPublicDevice } from 'modules/dealers/selectors';
import { ButtonProps, ButtonType } from 'components/ui/Button';

import Button from './Button';
import Carousel from './Carousel';
import { useSelector } from '../../hooks';
import DocumentLine from './DocumentLine';

interface AttachmentsProps {
  className?: Argument,
  documentClassName?: Argument,
  documents?: AbstractMedia[],
  singleDownloadTitle?: React.ReactNode,
  buttonProps?: ButtonProps,
  small?: boolean,
  alwaysPreviewable?: boolean,
  showDownloadInPreview?: boolean,
  hideDownloadInList?: boolean,
}

const Attachments: React.FC<AttachmentsProps> = ({
  className,
  documentClassName,
  singleDownloadTitle,
  buttonProps,
  documents = [],
  small,
  alwaysPreviewable,
  showDownloadInPreview,
  hideDownloadInList,
}) => {
  const isPublic = useSelector(isPublicDevice);
  const [previewMedia, setPreviewMedia] = React.useState<number>();

  const hasDocument = documents.length > 0;
  const hasManyDocuments = documents.length > 1;
  const displayDocumentsList = hasManyDocuments || isPublic || !singleDownloadTitle;
  const showDownloadButton = singleDownloadTitle && hasDocument && !hasManyDocuments && !isPublic;

  const handleMediaPreview = (index: number) => () => setPreviewMedia(index);

  const handleDownload = React.useCallback(() => {
    // In this case - We've only one item items in medias
    const [file] = documents;
    downloadURL(file.path, file.filename);
  }, [documents]);

  return (
    <>
      {/* We display a button if there are only one files and a list when we have many files */}
      {showDownloadButton && (
        <Button
          className="w-full mb-4"
          testId="download-button"
          onClick={handleDownload}
          type={ButtonType.PRIMARY}
          {...buttonProps}
        >
          {singleDownloadTitle}
        </Button>
      )}

      {displayDocumentsList && (
        <>
          <div className={classNames('flex flex-col justify-center mt-2', className)} data-testid="document-list">
            {documents.map((document, index) => (
              <div
                key={document.id}
                data-testid="document-list-item"
                className={classNames(documentClassName, small ? 'py-1' : 'py-6', (index !== 0 || !small) && 'border-t-2 border-gray-200')}
              >
                <DocumentLine
                  document={document}
                  hideDownload={isPublic || hideDownloadInList}
                  onPreview={(isPublic || alwaysPreviewable) ? handleMediaPreview(index) : undefined}
                  small={small}
                />
              </div>
            ))}
          </div>
          {previewMedia >= 0 && (
            <Carousel
              fullscreen
              hideActions
              showDownload={!isPublic && showDownloadInPreview}
              medias={documents}
              onClose={handleMediaPreview(undefined)}
              defaultMediaIndex={previewMedia}
            />
          )}
        </>
      )}
    </>
  );
};

export default Attachments;
